/* Shared WhatsApp-style chat primitives (bubbles + composer).
   Used by Admin MessagesTab and Recruiter RecruiterMessagesTab. */
import React, { useEffect, useRef, useState } from 'react';
import { api, getToken } from '../../lib/api';
import { Paperclip, Send, Loader2, FileText, X, Image as ImageIcon, CheckCheck, Download } from 'lucide-react';

const humanSize = (b) => {
  if (!b && b !== 0) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const MAX_PER_FILE = 15 * 1024 * 1024;
const MAX_FILES = 10;
export const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif', 'image/heic']);

/* Renders a single attachment inside a chat bubble. For images we lazy-fetch
   the authed bytes into a blob URL; for other files show a download card. */
const Attachment = ({ attachment, attUrl, onOpen }) => {
  const [src, setSrc] = useState(null);
  const isImg = attachment.is_image || IMAGE_TYPES.has(attachment.content_type);

  useEffect(() => {
    if (!isImg) return;
    let cancelled = false;
    let objUrl;
    (async () => {
      try {
        const token = getToken();
        const resp = await fetch(`${api.defaults.baseURL}${attUrl}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) throw new Error('fetch failed');
        const blob = await resp.blob();
        if (cancelled) return;
        objUrl = URL.createObjectURL(blob);
        setSrc(objUrl);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [attUrl, isImg]);

  const download = async () => {
    try {
      const resp = await api.get(attUrl, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = attachment.filename || 'file';
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { /* toast from caller */ }
  };

  if (isImg) {
    return (
      <button onClick={() => onOpen?.(src, attachment.filename)} type="button" className="block rounded-xl overflow-hidden bg-black/5 max-w-[260px] hover:opacity-95 transition-opacity" data-testid="chat-attachment-image">
        {src ? (
          <img src={src} alt={attachment.filename} className="w-full max-h-[260px] object-cover" />
        ) : (
          <div className="h-40 w-[240px] flex items-center justify-center text-xs text-[#6b7e6d]">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        )}
        <div className="px-2 py-1 text-[10px] text-white/80 bg-black/35 truncate">{attachment.filename} · {humanSize(attachment.size)}</div>
      </button>
    );
  }

  return (
    <button type="button" onClick={download} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/80 border border-[#0B5E2A]/15 hover:bg-white text-left max-w-[260px]" data-testid="chat-attachment-file">
      <div className="h-9 w-9 rounded bg-[#0B5E2A] text-white flex items-center justify-center shrink-0"><FileText className="h-4 w-4" /></div>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold text-[#0B5E2A] truncate">{attachment.filename}</div>
        <div className="text-[10px] text-[#6b7e6d]">{humanSize(attachment.size)} · {attachment.content_type?.split('/')[1]?.toUpperCase() || 'FILE'}</div>
      </div>
      <Download className="h-3.5 w-3.5 text-[#6b7e6d] shrink-0" />
    </button>
  );
};

/* Renders one message bubble with text and attachments. */
export const ChatBubble = ({ message, mine, readMark = true, attachmentUrl, onImageOpen }) => {
  const atts = message.attachments || [];
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[72%] px-3 py-2 rounded-2xl text-sm ${mine ? 'bg-[#0B5E2A] text-white rounded-br-sm' : 'bg-white border border-[#0B5E2A]/10 text-[#1b2a1f] rounded-bl-sm'}`}>
        {atts.length > 0 && (
          <div className={`space-y-1.5 ${message.content ? 'mb-2' : ''}`}>
            {atts.map((a, i) => (
              <Attachment key={i} attachment={a} attUrl={attachmentUrl(message.id, i)} onOpen={onImageOpen} />
            ))}
          </div>
        )}
        {message.content && (<div className="whitespace-pre-wrap break-words">{message.content}</div>)}
        <div className={`mt-1 flex items-center gap-1 text-[10px] ${mine ? 'text-white/70 justify-end' : 'text-[#6b7e6d]'}`}>
          <span>{new Date(message.created_at).toLocaleString()}</span>
          {mine && readMark && message.read_by_recruiter && (<CheckCheck className="h-3 w-3" data-testid="chat-read-mark" />)}
          {mine && readMark === 'recruiter-view' && message.read_by_admin && (<CheckCheck className="h-3 w-3" data-testid="chat-read-mark" />)}
        </div>
      </div>
    </div>
  );
};

/* Composer: text + multi-file attach + send. */
export const ChatComposer = ({ onSend, sending, disabled, placeholder = 'Type a message...' }) => {
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const addFiles = (fl) => {
    setError(null);
    const incoming = Array.from(fl || []);
    if (!incoming.length) return;
    const picked = [...files];
    for (const f of incoming) {
      if (picked.length >= MAX_FILES) { setError(`Max ${MAX_FILES} files`); break; }
      if (f.size > MAX_PER_FILE) { setError(`${f.name} > 15MB`); continue; }
      picked.push(f);
    }
    setFiles(picked);
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e?.preventDefault?.();
    if ((!text.trim() && files.length === 0) || sending || disabled) return;
    const ok = await onSend({ content: text.trim(), files });
    if (ok) { setText(''); setFiles([]); }
  };

  return (
    <form onSubmit={submit} className="border-t border-[#0B5E2A]/10 bg-white" data-testid="chat-composer">
      {files.length > 0 && (
        <div className="px-3 pt-3 flex gap-2 flex-wrap" data-testid="chat-attachment-previews">
          {files.map((f, i) => {
            const isImg = f.type && f.type.startsWith('image/');
            return (
              <div key={i} className="relative flex items-center gap-2 bg-[#f0f6ee] border border-[#0B5E2A]/15 rounded-lg pl-2 pr-8 py-1.5 max-w-[240px]" data-testid={`chat-attachment-preview-${i}`}>
                <div className="h-8 w-8 rounded bg-[#0B5E2A] text-white flex items-center justify-center shrink-0">
                  {isImg ? <ImageIcon className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-[#0B5E2A] truncate max-w-[160px]">{f.name}</div>
                  <div className="text-[10px] text-[#6b7e6d]">{humanSize(f.size)}</div>
                </div>
                <button type="button" onClick={() => removeFile(i)} aria-label="Remove attachment" className="absolute top-1 right-1 h-5 w-5 rounded-full bg-white hover:bg-[#FDE2E2] text-[#9b1c1c] flex items-center justify-center"><X className="h-3 w-3" /></button>
              </div>
            );
          })}
        </div>
      )}
      {error && <div className="px-3 pt-2 text-xs text-[#9b1c1c]">{error}</div>}
      <div className="p-3 flex gap-2 items-end">
        <label className="shrink-0 h-10 w-10 rounded-full border border-[#0B5E2A]/25 text-[#0B5E2A] hover:bg-[#f0f6ee] flex items-center justify-center cursor-pointer" title="Attach file" data-testid="chat-attach-btn">
          <Paperclip className="h-4 w-4" />
          <input ref={inputRef} type="file" className="hidden" multiple onChange={(e) => addFiles(e.target.files)} data-testid="chat-file-input" />
        </label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
          }}
          rows={1}
          placeholder={placeholder}
          disabled={disabled}
          data-testid="chat-text-input"
          className="flex-1 resize-none max-h-28 px-3 py-2.5 rounded-2xl border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30 disabled:bg-[#f5f5f5]"
        />
        <button type="submit" disabled={sending || disabled || (!text.trim() && files.length === 0)} data-testid="chat-send-btn" className="shrink-0 h-10 w-10 rounded-full bg-[#0B5E2A] text-white hover:bg-[#094a22] disabled:opacity-50 flex items-center justify-center transition-colors">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 pointer-events-none" />}
        </button>
      </div>
    </form>
  );
};

/* Minimal image lightbox. */
export const ImageLightbox = ({ src, name, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  if (!src) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-6" onClick={onClose} data-testid="image-lightbox">
      <img src={src} alt={name || 'preview'} className="max-h-[90vh] max-w-[90vw] rounded-xl shadow-2xl" onClick={(e) => e.stopPropagation()} />
      <button onClick={onClose} className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/20 hover:bg-white/35 text-white flex items-center justify-center"><X className="h-5 w-5" /></button>
    </div>
  );
};
